/**
 * "Delete folder" against the mail server itself.
 *
 * Its own dialog rather than the shared `ConfirmModal`, for two reasons that
 * both matter here. The question needs a number - a folder holding 812 messages
 * and an empty one are not the same decision, and the rail already knows the
 * count. And the server can still refuse after the operator has said yes (the
 * folder moved, it gained a child, the provider said no), which needs somewhere
 * to be read; `ConfirmModal` has no room for an answer.
 *
 * There is no undo. IMAP DELETE removes the mailbox on the server and the CRM
 * keeps no copy of what was inside, so the sentence says that plainly instead
 * of asking a polite "are you sure".
 */
import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { deleteImapFolder, type ImapFolder } from "../../../../lib/mail";
import { Btn, Modal } from "../../../ui";
import { imapFolderError } from "./imapFolderErrors";

export function DeleteImapFolderModal({
  mailbox,
  folder,
  onClose,
  onDeleted,
}: {
  /** The mailbox the folder is in: "shared" or an own mailbox id. */
  mailbox: string;
  folder: ImapFolder;
  onClose: () => void;
  onDeleted: (path: string) => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteImapFolder(folder.path, mailbox);
      onDeleted(folder.path);
      onClose();
    } catch (err) {
      setError(imapFolderError(t, err, "email.imap.deleteFailed"));
    } finally {
      setBusy(false);
    }
  }

  // Null means the count was never asked for - above forty folders the list
  // skips it (one STATUS round trip each). An unknown count is said as unknown
  // rather than drawn as zero, which would read as "this folder is empty". An
  // empty folder gets its own line too: "holds 0 messages, and they go with the
  // folder" is the plural machinery talking, not a sentence anyone would write.
  const count = folder.messages;
  const body =
    count === null
      ? t("email.imap.deleteBodyUnknown")
      : count === 0
        ? t("email.imap.deleteBodyEmpty")
        : t("email.imap.deleteBody", { n: count });

  return (
    <Modal
      title={t("email.imap.deleteTitle")}
      label={t("email.imap.deleteTitle")}
      onClose={onClose}
      className="max-w-[440px]"
    >
      <p className="crm-sub mt-1">{t("email.imap.deleteLead", { path: folder.path })}</p>
      <p className="mt-2 text-[12.5px] leading-snug font-medium">{body}</p>

      {error ? <p className="mt-2 text-[12.5px] leading-snug text-destructive">{error}</p> : null}

      <div className="crm-m-foot">
        <Btn type="button" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </Btn>
        <Btn type="button" tone="danger" onClick={() => void run()} disabled={busy}>
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
          {t("email.imap.delete")}
        </Btn>
      </div>
    </Modal>
  );
}
